/**
 * @file This file contains the appointment API functions.
 * @module api/appointmentApi
 * 
 * Updated to handle timezone conversions consistently and appointment types
 */
import { ENV } from '../../../config/runtimeEnv';
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { Appointment, AppointmentSaveRequest, CompletedAppointment, AppointmentType, AppointmentTypeUsage, AvailableTimesResponse, AppointmentPractitioner } from "../../../types/appointments/AppointmentTypes";
import { PatientInfo } from "../../../pages/patient/BookingForm";
import { DateTimeUtils, parseUTCToLocal, localToUTCString } from "../../../utils/userPrefs/DateTimeUtils";
import { deviceZone } from "../../../utils/time";
import { fetchPatientsByIds } from "../PatientApi";
import type { UserUuid } from "../../../types/identity";
import { http } from "../../../lib/http/client";
import { httpFetch } from "../../../lib/http/fetch";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/**
 * Convert appointment data from backend (UTC) to local timezone for display
 */
const convertAppointmentFromBackend = (appointment: any): Appointment => {
	return {
		...appointment,
		start_time: parseUTCToLocal(appointment.start_time),
		end_time: parseUTCToLocal(appointment.end_time),
		created_at: parseUTCToLocal(appointment.created_at) || new Date(),
		updated_at: parseUTCToLocal(appointment.updated_at) || new Date(),
		appointment_type_id: appointment.appointment_type_id,
		cliniko_id: appointment.cliniko_id,
		cliniko_data: appointment.cliniko_data,
		cliniko_synced_at: appointment.cliniko_synced_at,
		created_in_cliniko: appointment.created_in_cliniko,
		cliniko_booking_link: appointment.cliniko_booking_link,
	};
};

/**
 * Convert appointment data for sending to backend (local to UTC)
 */
const convertAppointmentForBackend = (appointment: Partial<AppointmentSaveRequest>): any => {
	// booking_timezone is the PATIENT's capture, taken once at booking: a practitioner edit must
	// never re-stamp it, so this path sends nothing and healthcare's resolver decides.
	const { booking_timezone: _capturedAtBooking, ...rest } = appointment;

	return {
		...rest,
		start_time: localToUTCString(appointment.start_time),
		end_time: localToUTCString(appointment.end_time),
		date: localToUTCString(appointment.date),
	};
};

export const fetchAppointments = async (start_date: Date, end_date: Date, practitionerId?: UserUuid, patientId?: UserUuid, ): Promise<{
	data: Appointment[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointments`,
		{
			params: {
				practitioner_id: practitionerId,
				patient_id: patientId,
				start_date: start_date,
				end_date: end_date
			},
		}
	);

	const appointments = response.data.data.map(convertAppointmentFromBackend);

	return {
		data: appointments,
	};
};

export interface ConflictingAppointment {
	id: number;
	start: Date;
	end: Date;
	patient: string;
	practitionerIds: UserUuid[];
}

const CONFLICT_IGNORED_STATUSES = ["Cancelled", "Canceled"];

const practitionerIdsFor = (appointment: Appointment): UserUuid[] => {
	const raw = [appointment.practitioner_id, ...(appointment.practitioners ?? []).map((practitioner) => practitioner.practitioner_id)];
	return [...new Set(raw.filter(Boolean))];
};

/**
 * Appointments a would-be unavailable block would sit on top of — the S11 warning only.
 * Reuses the practitioner appointments read; healthcare gains no route and nothing is written.
 * A null practitionerId asks about the whole clinic, which is what a clinic-wide closure needs.
 */
export const fetchConflictingAppointments = async (practitionerId: UserUuid | null, start: Date, end: Date): Promise<ConflictingAppointment[]> => {
	const { data } = await fetchAppointments(start, end, practitionerId ?? undefined);

	const overlapping = data.filter((appointment) => {
		if (!appointment.start_time || !appointment.end_time) return false;
		if (CONFLICT_IGNORED_STATUSES.includes(appointment.status)) return false;

		return appointment.start_time.getTime() < end.getTime() && appointment.end_time.getTime() > start.getTime();
	});

	const names = new Map<UserUuid, string>();
	try {
		const patients = await fetchPatientsByIds([...new Set(overlapping.map((appointment) => appointment.patient_id).filter(Boolean))]);
		patients.forEach((patient) => names.set(patient.id, `${patient.first_name} ${patient.last_name}`.trim()));
	} catch {
		// A name lookup failure must not swallow the safety warning; the title stands in.
	}

	return overlapping.map((appointment) => ({
		id: appointment.id,
		start: appointment.start_time as Date,
		end: appointment.end_time as Date,
		patient: names.get(appointment.patient_id) || appointment.title || "Unknown patient",
		practitionerIds: practitionerIdsFor(appointment),
	}));
};

// Only what the new range NEWLY covers is worth warning about — an appointment already inside the block stays inside it.
export const newlyConflicting = (conflicts: ConflictingAppointment[], previous?: { start: Date; end: Date } | null): ConflictingAppointment[] =>
	previous ? conflicts.filter((conflict) => conflict.start.getTime() >= previous.end.getTime() || conflict.end.getTime() <= previous.start.getTime()) : conflicts;

// A null target is the clinic-closure bucket — every conflict applies; an id bucket is a tandem-aware membership test.
export const conflictsForPractitioners = (conflicts: ConflictingAppointment[], targets: Array<UserUuid | null>): Map<UserUuid | null, ConflictingAppointment[]> =>
	new Map(targets.map((target) => [target, target === null ? conflicts : conflicts.filter((conflict) => conflict.practitionerIds.includes(target))]));

export const fetchSingleAppointmentById = async (
	id: number
): Promise<{
	data: {ready:boolean, appointment: Appointment };
}> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/appointments/single/${id}`);

	const appointment = convertAppointmentFromBackend(response.data);

	return {
		data: {
			ready: response.data.ready,
			appointment: appointment
		},
	};
};

export const getAppointmentFromConsultLink = async (
	link: string
): Promise<{
	data: any;
}> => {
	const response = await http.post(`${API_BASE_URL}/public/appointments/room`, {
		consultation_link: link,
	});

	return {
		data: response.data,
	};
};

export const setNewAppointment = async (
	appointmentData: AppointmentSaveRequest,
	acknowledgeUnavailable = false,
): Promise<{
	data: Appointment;
	message: string;
}> => {
	const user = getSessionUser();

	// healthcare reads `send_confirmation` off the request root, not out of `appointmentData`
	const { send_confirmation, ...appointmentFields } = appointmentData;
	const backendData = convertAppointmentForBackend(appointmentFields);

	const response = await http.post(`${API_BASE_URL}/practitioner/appointments`, {
		appointmentData: backendData,
		user_id: user.uuid,
		// omitted when the caller has no opinion, so healthcare's own default applies
		...(send_confirmation === undefined ? {} : { send_confirmation }),
		// the practitioner's answer to healthcare's unavailable_block 409 — a request-root key too
		...(acknowledgeUnavailable ? { acknowledge_unavailable: true } : {}),
	});

	const appointment = convertAppointmentFromBackend(response.data.data);

	return {
		data: appointment,
		message: response.data.message,
	};
};

export const updateAppointment = async (
	appointmentData: AppointmentSaveRequest,
	acknowledgeUnavailable = false,
): Promise<{
	data: Appointment;
	message: string;
}> => {
	const user = getSessionUser();

	const { send_confirmation, ...appointmentFields } = appointmentData;
	const backendData = convertAppointmentForBackend(appointmentFields);

	const response = await http.put(`${API_BASE_URL}/practitioner/appointments/${appointmentData.id}`, {
		appointmentData: backendData,
		user_id: user.uuid,
		...(send_confirmation === undefined ? {} : { send_confirmation }),
		...(acknowledgeUnavailable ? { acknowledge_unavailable: true } : {}),
	});

	const appointment = convertAppointmentFromBackend(response.data.data);

	return {
		data: appointment,
		message: response.data.message,
	};
};

export const deleteAppointment = async (appointmentId: number) => {
	const response = await http.delete(`${API_BASE_URL}/practitioner/appointments/${appointmentId}`);
	return response.data;
};












/////////////////////////////////////    Completed appointments    //////////////////////////////////////

const convertCompletedAppointmentFromBackend = (appointment: any): CompletedAppointment => {
	return {
		...appointment,
		created_at: parseUTCToLocal(appointment.created_at) || new Date(),
		updated_at: parseUTCToLocal(appointment.updated_at) || new Date(),
	};
};

export const fetchCompletedAppointments = async (practitionerId?: UserUuid, patientId?: UserUuid): Promise<{
	data: CompletedAppointment[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointments/completed`,
		{
			params: {
				practitioner_id: practitionerId,
				patient_id: patientId
			},
		}
	);

	const appointments = response.data.data.map(convertCompletedAppointmentFromBackend);

	return {
		data: appointments,
	};
};

export const fetchSingleCompletedAppointmentById = async (
	id: number
): Promise<{
	data: CompletedAppointment;
}> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/appointments/completed/single/${id}`);

	const appointment = convertCompletedAppointmentFromBackend(response.data);

	return {
		data: appointment,
	};
};






////////////////////////////////////////////  Appointment Types

/**
 * Fetch all bookable appointment types
 */
export const fetchBookableAppointmentTypes = async (): Promise<AppointmentType[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointment-types/bookable/list`
	);

	return response.data.data || [];
};

export const fetchBookableAppointmentTypesPatient = async (patientId:UserUuid = null): Promise<AppointmentType[]> => {
	const response = await http.get(
		`${API_BASE_URL}/patient/appointment-types/bookable/list`,
		{
			params: {
				patient_id: patientId
			},
		}
	);

	return response.data.data || [];
};

/**
 * Fetch all appointment types (authenticated)
 */
export const fetchAppointmentTypes = async (): Promise<AppointmentType[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointment-types`
	);

	return response.data.data || [];
};


/**
 * Fetch all appointment types (authenticated)
 */
export const fetchAppointmentTypesWithoutPractitioners = async (): Promise<AppointmentType[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointment-types-basic`
	);

	return response.data.data || [];
};


/**
 * One patient's booking usage against every CAPPED appointment type (S10, ClickUp 86d3yhdwc).
 *
 * ⚠️ CAPPED TYPES ONLY — an uncapped type is absent from the list, which is what tells the UI to
 * render nothing rather than "Used 3 of unlimited". Never substitute a client-side count over an
 * appointment list: `bookings_used` normalises the free-text `status` server-side.
 */
export const fetchPatientAppointmentTypeUsage = async (patientId: UserUuid): Promise<AppointmentTypeUsage[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/patients/${patientId}/appointment-type-usage`
	);

	return response.data.data || [];
};

export const getPatientAppointmentTypes = async (patientId: UserUuid): Promise<AppointmentType[]> => {
	const response = await http.get(
		`${API_BASE_URL}/patient/appointment-types/patient/list/${patientId}`
	);

	return response.data.data || [];
};

export const assignAppointmentTypeToPatient = async (appointmentTypeId: number[], patientId: UserUuid): Promise<void> => {
	await http.post(
		`${API_BASE_URL}/patient/appointment-types/patient/assign`,
		{
			appointment_type_id: appointmentTypeId,
			patient_id: patientId,
		}
	);
};

export const removeAppointmentTypeFromPatient = async (appointmentTypeIds: number[], patientId: UserUuid): Promise<void> => {
	await http.post(
		`${API_BASE_URL}/patient/appointment-types/patient/remove`,
		{
			appointment_type_id: appointmentTypeIds,
			patient_id: patientId,
		}
	);
};






////////////////////////////////////////////  Booking form

/**
 * Fetch available times for a specific appointment type.
 *
 * NO `days_ahead` parameter: the horizon is the clinic's own `booking_window_days`, which the
 * backend applies by default (S4). Sending one could only ever NARROW the window — it is clamped
 * server-side and can never widen it — so the hardcoded `days_ahead: '30'` this replaces silently
 * capped every clinic at 30 days no matter what they had configured.
 *
 * The window the backend actually used comes back on `policy.booking_window_days`, and the
 * timezone the clinic's hours are defined in on `policy.timezone` (both absent on a 404).
 */
export const fetchAvailableTimes = async (appointmentTypeId: number, accessToken?: string, screeningToken?: string): Promise<AvailableTimesResponse> => {
	const url = `${API_BASE_URL}/patient/bookings`;

	const params = new URLSearchParams({
		appointment_type_id: appointmentTypeId.toString(),
	});

	// `/patient/bookings` is the ANONYMOUS availability read and does not sit behind
	// `booking.token` — the token is carried so the two GETs a tokenised visit makes look
	// alike in a proxy/access log, and so this call keeps working unchanged if the route is
	// ever moved inside the group. It is never a substitute for the credential on the POST.
	if (accessToken) {
		params.set("t", accessToken);
	}
	const response = await httpFetch(`${url}?${params.toString()}`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			...screeningTokenHeaders(screeningToken),
		}
	});

	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}

	const data: AvailableTimesResponse = await response.json();

	// Convert time slots to include local timezone information
	if (data.practitioners) {
		data.practitioners = data.practitioners.map((practitioner: any) => ({
			...practitioner,
			Available_times: practitioner.Available_times.map((day: any) => ({
				...day,
				slots: day.slots.map((slot: any) => DateTimeUtils.convertTimeSlotToLocal(slot))
			}))
		}));
	}

	return data;
};

export const createAppointment = async (data: Partial<AppointmentSaveRequest>, patientInfo: PatientInfo, screeningToken?: string) => {
	const timeSlotUTC = localToUTCString(data.start_time);

	const response = await http.post(`${API_BASE_URL}/patient/bookings`, {
		'patient_first_name': patientInfo.first_name,
		'patient_last_name': patientInfo.last_name,
		'patient_email': patientInfo.email,
		'patient_phone': patientInfo.phone,
		'practitioner_id': data.practitioner_id,
		'appointment_type_id': data.appointment_type_id,
		'date': localToUTCString(data.date),
		'time_slot': timeSlotUTC,
		'category': data.category,
		// The patient's own device zone, and the ONLY capture sent — healthcare persists it as
		// the appointment's booking_timezone. A second field holding it is how the two drift.
		'patient_timezone': deviceZone(),
		'consultation_link': data.consultation_link || "",
	}, {
		headers: { ...screeningTokenHeaders(screeningToken) },
	});
	return response.data;
};

////////////////////////////////////////////  Tokenised booking link (S9, ClickUp 86d3yhdw9)

/**
 * Everything `GET /api/patient/bookings/link` returns, and NOTHING else — the controller
 * forwards exactly four patient fields out of a much wider auth-service payload (no DOB, no
 * Medicare number, no address), and that narrowing is the contract, not an oversight. Do not
 * widen this interface in the hope the backend fills it in.
 */
export interface BookingLinkContext {
	/** Every field is nullable: an auth-service outage yields nulls, never a 5xx. */
	patient: {
		first_name: string | null;
		last_name: string | null;
		email: string | null;
		phone: string | null;
	};
	/** Null on an UNSCOPED link — the patient picks the type themselves. */
	appointment_type_id: number | null;
	practitioner_id: UserUuid | null;
	expires_at: string | null;
	/** Null when the type is uncapped OR when the link is unscoped (nothing to count against). */
	remaining: number | null;
}

/**
 * TRANSPORT — mirrors `formApi.ts`'s form-token split, and the split is deliberate:
 *   • GETs put the token on the query string (`?t=`). A patient pastes those URLs into a
 *     browser bar; a header is not expressible there.
 *   • POSTs put it in the `X-Booking-Token` header, which keeps a live capability out of
 *     server access logs. `EnsureBookingLinkToken` reads the query with `query('t')`, never
 *     from the POST body, so a body token is not a way in. The header wins server-side when
 *     both are present.
 *
 * The booking token is the whole credential: the visitor usually has no session, and the
 * gateway serves these as `capability` routes (contract §4.3).
 */
const bookingTokenParams = (t?: string) => (t ? { t } : undefined);
export const bookingTokenHeaders = (t?: string) => (t ? { "X-Booking-Token": t } : undefined);

/** The screening pass (S6). Returns undefined so a spread OMITS the header rather than sending undefined. */
export const screeningTokenHeaders = (t?: string) => (t ? { "X-Screening-Token": t } : undefined);

/**
 * The booking form's bootstrap read. Refusals are `EnsureBookingLinkToken`'s:
 * 404 (missing / unknown / revoked), 410 `link_expired`, 410 `already_booked` — map them with
 * `classifyBookingLinkError` rather than reading the status at the call site.
 */
export const getBookingLinkContext = async (accessToken?: string): Promise<BookingLinkContext> => {
	const response = await http.get(`${API_BASE_URL}/patient/bookings/link`, {
		params: bookingTokenParams(accessToken),
		headers: {},
	});
	return response.data;
};

/**
 * The tokenised twin of `createAppointment`, which atomically consumes the link.
 *
 * 🚨 NO `patient_*` FIELDS. `bookAppointmentViaLink()` never consults the body for identity —
 * `$link->patient_id` decides it — and sending them would imply they mattered, which is
 * exactly the misunderstanding that makes a per-patient cap defeatable by typing a different
 * name. The locked fields on the form are a UI courtesy; THIS is the enforcement.
 *
 * A replay answers 410 `already_booked`; an exhausted cap answers 422 carrying the same
 * message string. They are different outcomes — see `bookingLinkState.ts`.
 */
export const createAppointmentViaLink = async (data: Partial<AppointmentSaveRequest>, accessToken?: string, screeningToken?: string) => {
	const response = await http.post(`${API_BASE_URL}/patient/bookings/link`, {
		'practitioner_id': data.practitioner_id,
		'appointment_type_id': data.appointment_type_id,
		'date': localToUTCString(data.date),
		'time_slot': localToUTCString(data.start_time),
		'category': data.category,
		// Same single capture as the anonymous path — the link supplies identity, not a zone.
		'patient_timezone': deviceZone(),
		'consultation_link': data.consultation_link || "",
	}, {
		headers: { ...bookingTokenHeaders(accessToken), ...screeningTokenHeaders(screeningToken) },
	});
	return response.data;
};

export const fetchAppointmentPractitioners = async (appointmentId: number): Promise<AppointmentPractitioner[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointments/${appointmentId}/practitioners`
	);
	return response.data.data;
};

export const addPractitionerToAppointment = async (
	appointmentId: number,
	data: { practitioner_id: UserUuid; role?: 'primary' | 'secondary'; timing_position?: 'before' | 'during' | 'after'; timing_minutes?: number }
): Promise<AppointmentPractitioner> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointments/${appointmentId}/practitioners`,
		data
	);
	return response.data.data;
};

export const removePractitionerFromAppointment = async (
	appointmentId: number,
	practitionerId: UserUuid
): Promise<void> => {
	await http.delete(
		`${API_BASE_URL}/practitioner/appointments/${appointmentId}/practitioners/${practitionerId}`
	);
};